<script setup>
import { ref, computed, watch, onUnmounted } from 'vue';

const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  options: { type: Array, default: () => [] },
});
const emit = defineEmits(['update:modelValue']);

const showDropdown = ref(false);
const labelSearch = ref('');
const labelHighlight = ref(0);

const filteredOptions = computed(() => {
  const q = labelSearch.value.toLowerCase();
  if (!q) return props.options;
  return props.options
    .filter(o => o.label.toLowerCase().includes(q))
    .sort(
      (a, b) =>
        a.label.toLowerCase().indexOf(q) - b.label.toLowerCase().indexOf(q)
    );
});

const labelNavigable = computed(() =>
  filteredOptions.value.map(o => ({ id: o.value }))
);

watch(labelNavigable, () => {
  labelHighlight.value = 0;
});

function chipStyle(color) {
  if (!color)
    return { backgroundColor: 'rgba(99,102,241,0.12)', color: '#4338ca' };
  return { backgroundColor: `${color}22`, color };
}

function isActive(value) {
  return props.modelValue.includes(value);
}

function toggle(value) {
  const current = props.modelValue;
  const updated = current.includes(value)
    ? current.filter(v => v !== value)
    : [...current, value];
  emit('update:modelValue', updated);
}

function remove(value) {
  emit(
    'update:modelValue',
    props.modelValue.filter(v => v !== value)
  );
}

function onKeydown(e) {
  if (!showDropdown.value) return;
  if (e.key === 'Escape') {
    showDropdown.value = false;
    labelSearch.value = '';
  } else if (e.key === 'ArrowDown') {
    e.preventDefault();
    labelHighlight.value = Math.min(
      labelHighlight.value + 1,
      labelNavigable.value.length - 1
    );
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    labelHighlight.value = Math.max(labelHighlight.value - 1, 0);
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const item = labelNavigable.value[labelHighlight.value];
    if (item) toggle(item.id);
  } else if (e.key === 'Backspace') {
    labelSearch.value = labelSearch.value.slice(0, -1);
  } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
    labelSearch.value += e.key;
  }
}

watch(showDropdown, val => {
  if (val) {
    labelHighlight.value = 0;
    window.addEventListener('keydown', onKeydown);
  } else {
    labelSearch.value = '';
    window.removeEventListener('keydown', onKeydown);
  }
});

onUnmounted(() => window.removeEventListener('keydown', onKeydown));
</script>

<template>
  <div class="flex flex-col gap-1 relative" @click.stop>
    <!-- Chips + botão abrir -->
    <div class="flex items-center gap-1 flex-wrap">
      <span
        v-for="value in modelValue"
        :key="value"
        class="group/chip inline-flex items-center text-[11px] font-medium px-1.5 py-px rounded-full"
        :style="chipStyle(options.find(o => o.value === value)?.color)"
      >
        {{ options.find(o => o.value === value)?.label ?? value }}
        <span
          class="overflow-hidden transition-all duration-150 max-w-0 group-hover/chip:max-w-[12px] flex items-center justify-center"
        >
          <button
            class="flex items-center justify-center hover:bg-black/10 rounded-full ml-[2px]"
            @click.stop="remove(value)"
          >
            <span class="i-lucide-x size-[8px] flex-shrink-0" />
          </button>
        </span>
      </span>
      <button
        type="button"
        class="flex items-center gap-1 text-sm outline-none focus:outline-none"
        :class="
          showDropdown ? 'text-n-brand' : 'text-n-slate-10 hover:text-n-brand'
        "
        @click="showDropdown = !showDropdown"
      >
        <span class="i-lucide-tag size-3.5 flex-shrink-0" />
        <span
          v-if="showDropdown && labelSearch"
          class="text-n-slate-10 text-xs"
          >{{ labelSearch }}</span
        >
      </button>
    </div>

    <!-- Overlay para fechar ao clicar fora -->
    <div
      v-if="showDropdown"
      class="fixed inset-0 z-20"
      @click="showDropdown = false"
    />

    <!-- Dropdown -->
    <div
      v-if="showDropdown"
      class="absolute left-0 top-full mt-1 z-30 bg-n-background border border-n-weak rounded-xl shadow-xl w-52 py-1 overflow-hidden"
      @click.stop
    >
      <button
        v-for="(opt, idx) in filteredOptions"
        :key="opt.value"
        class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors focus:outline-none"
        :class="
          idx === labelHighlight
            ? 'text-n-brand font-medium bg-n-alpha-1'
            : isActive(opt.value)
              ? 'text-n-brand font-medium'
              : 'text-n-slate-12'
        "
        @click="toggle(opt.value)"
      >
        <span
          class="size-2.5 rounded-full flex-shrink-0"
          :style="{ backgroundColor: opt.color || '#6366f1' }"
        />
        <span class="truncate">{{ opt.label }}</span>
        <span
          v-if="isActive(opt.value)"
          class="i-lucide-check size-3 ml-auto flex-shrink-0 text-n-brand"
        />
      </button>
      <p
        v-if="labelSearch && filteredOptions.length === 0"
        class="px-3 py-2 text-sm text-n-slate-10 italic"
      >
        {{ $t('KANBAN.AUTOMATIONS.NOTHING_FOUND') }}
      </p>
    </div>
  </div>
</template>
